import type {
  LightReachActionView,
  LightReachApplicationView,
  LightReachContractView,
  LightReachStipulationView,
  LightReachSupplementalView,
} from "./lightreach-kind-bundle"
import type { ConcertOutcomeTone } from "../concert/concert-application-copy"

export const LIGHTREACH_APPLICATION_TITLE = `LightReach application`
export const LIGHTREACH_RESERVE_NOW_REASON_FALLBACK = `Save a current LightReach offer before applying`
export const LIGHTREACH_NO_APPLICATION_MESSAGE = `No LightReach application yet. Use Reserve now on the proposal page to apply.`
export const LIGHTREACH_REFRESH_STATUS_LABEL = `Refresh status`
export const LIGHTREACH_REFRESHING_STATUS_LABEL = `Refreshing status`
export const LIGHTREACH_REFRESH_STATUS_ERROR = `Unable to refresh the application status. Try again.`
export const LIGHTREACH_STIPULATIONS_HEADING = `Stipulations`
export const LIGHTREACH_NO_STIPULATIONS_MESSAGE = `LightReach has not reported any stipulations.`
export const LIGHTREACH_NEXT_STEP_LABEL = `Next step`
export const LIGHTREACH_DISCLOSURES_UNAVAILABLE = `LightReach disclosures could not be loaded, so the application cannot be submitted. Close and try again.`
export const LIGHTREACH_APPLICATION_EXISTS_REASON = `A LightReach application exists for this proposal, so its offer cannot be replaced.`
export const LIGHTREACH_SUPPORT_GUIDANCE = `Contact Powur support before taking any further action on this application.`
export const LIGHTREACH_CONTRACT_HEADING = `Contract`
export const LIGHTREACH_SEND_CONTRACT_LABEL = `Send contract`
export const LIGHTREACH_SENDING_CONTRACT_LABEL = `Sending contract`
export const LIGHTREACH_REFRESH_CONTRACT_LABEL = `Refresh contract`
export const LIGHTREACH_REFRESHING_CONTRACT_LABEL = `Refreshing contract`
export const LIGHTREACH_RETRY_SUPPLEMENTAL_LABEL = `Retry supplemental documents`
export const LIGHTREACH_RETRYING_SUPPLEMENTAL_LABEL = `Retrying supplemental documents`
export const LIGHTREACH_CONTRACT_ACTION_ERROR = `Vision could not confirm the request reached LightReach. Refresh the contract before trying again.`
export const LIGHTREACH_NO_CONTRACT_MESSAGE = `No LightReach contract has been sent.`

export type LightReachOutcome = {
  badge: string
  tone: ConcertOutcomeTone
  title: string
  detail: string
  nextStep: string
  /** Support is the only next step; nothing on the proposal fixes it. */
  supportNeeded: boolean
}

const SEND_CONTRACT_STEP = `Send the LightReach contract below.`
const CONTRACT_UNAVAILABLE_STEP = `The LightReach contract cannot be sent from this proposal yet.`
const STIPULATION_STEP = `Resolve the open stipulations below with LightReach. Their status updates here as LightReach reviews them.`
const CHOOSE_ANOTHER_PARTNER_STEP = `Choose another Finance Partner for this proposal, or contact Powur support.`
const REAPPLY_STEP = `Re-apply below once the homeowner is ready, or choose another Finance Partner.`

/** What a kind that offers Re-apply says when the server withholds it: its reason, then Re-apply. */
const reapplyBlockedStep = (reason: string): string =>
  `${reason.replace(/\.$/, ``)}, then re-apply below.`

/**
 * What only some kinds report or offer (Solar does; Battery doesn't), so a
 * kind without them reads exactly as before.
 */
export type LightReachOutcomeOptions = {
  /** Re-apply, as the server allows it now; absent where the kind has none. */
  readonly reapply?: LightReachActionView
  /** LightReach expires the credit decision, and the kind shows it. */
  readonly reportsExpiry?: boolean
  /** A new contract may be sent after a voided or rejected one, when the server allows it. */
  readonly resendAfterClose?: boolean
}

export const LIGHTREACH_REAPPLY_LABEL = `Re-apply`
export const LIGHTREACH_VOID_CONTRACT_LABEL = `Void contract`
export const LIGHTREACH_VOIDING_CONTRACT_LABEL = `Voiding contract`
export const LIGHTREACH_CONTRACT_NEEDS_ATTENTION_LABEL = `Needs attention`

export type LightReachContractState = {
  badge: string
  tone: ConcertOutcomeTone
  detail: string
  nextStep: string
  supportNeeded: boolean
}

const SIGNED_STATUSES: ReadonlySet<string> = new Set([`signed`, `approved`])
const CLOSED_STATUSES: ReadonlySet<string> = new Set([
  `voided`,
  `rejected`,
  `superseded`,
])

/** LightReach voided, rejected or replaced the contract. */
export const lightReachContractClosed = (
  contract: LightReachContractView,
): boolean => CLOSED_STATUSES.has(contract.status)

/**
 * Where the contract stands, from what LightReach last reported. Steps name
 * Send or Refresh contract only for an actor the server lets do it, and a new
 * contract only where the server lets the seller send one after a closed one.
 */
export const lightReachContractState = (
  contract: LightReachContractView,
  actions: {
    send: LightReachActionView
    refresh: LightReachActionView
  },
  { canSendAgain = false }: { canSendAgain?: boolean } = {},
): LightReachContractState => {
  const canRefresh = actions.refresh.allowed
  if (lightReachContractClosed(contract)) {
    return {
      badge: `Closed`,
      tone: `danger`,
      detail: `LightReach reports the contract as ${contract.status}.`,
      nextStep: canSendAgain
        ? `Send a new LightReach contract below.`
        : LIGHTREACH_SUPPORT_GUIDANCE,
      supportNeeded: !canSendAgain,
    }
  }
  if (contract.handedOff) {
    return {
      badge: `Signed`,
      tone: `success`,
      detail: `The homeowner signed the contract, and PowurOS has the signed deal.`,
      nextStep: `Nothing more is needed on the contract.`,
      supportNeeded: false,
    }
  }
  if (contract.signedAt != null || SIGNED_STATUSES.has(contract.status)) {
    return {
      badge: `Signed`,
      tone: `success`,
      detail: `The homeowner signed the contract.`,
      nextStep: canRefresh
        ? `Vision hands the signed deal to PowurOS. If this does not update, refresh the contract.`
        : `Vision hands the signed deal to PowurOS.`,
      supportNeeded: false,
    }
  }
  if (contract.delivered) {
    return {
      badge: `Sent`,
      tone: `gold`,
      detail: `LightReach sent the contract to the homeowner.`,
      nextStep: `Wait for the homeowner to sign the contract LightReach sent them.`,
      supportNeeded: false,
    }
  }
  // LightReach drafts the contract with the quote; a failed send leaves it here.
  if (contract.status === `created`) {
    return {
      badge: `Not sent`,
      tone: `muted`,
      detail: `LightReach drafted the contract but has not sent it to the homeowner.`,
      nextStep: actions.send.allowed
        ? SEND_CONTRACT_STEP
        : (actions.send.reason ?? CONTRACT_UNAVAILABLE_STEP),
      supportNeeded: false,
    }
  }
  return {
    badge: `Sending`,
    tone: `muted`,
    detail: `LightReach accepted the request and is sending the contract.`,
    nextStep: canRefresh
      ? `Refresh the contract in a few minutes to confirm LightReach sent it.`
      : `LightReach confirms delivery in a few minutes; check back then.`,
    supportNeeded: false,
  }
}

const SUPPLEMENTAL_STATE_LABEL: Record<string, string> = {
  claimed: `Sending`,
  sent: `Sent`,
  not_applicable: `Not needed`,
  failed: `Failed`,
  blocked: `Blocked`,
  send_unknown: `Unconfirmed`,
  superseded: `Replaced`,
}

export const lightReachSupplementalState = (
  supplemental: LightReachSupplementalView,
): { label: string; tone: ConcertOutcomeTone } => ({
  label: SUPPLEMENTAL_STATE_LABEL[supplemental.state] ?? supplemental.state,
  tone:
    supplemental.state === `sent` || supplemental.state === `not_applicable`
      ? `success`
      : supplemental.state === `failed` || supplemental.state === `blocked`
        ? `danger`
        : `gold`,
})

/**
 * One reading of LightReach's own status. The mapped loan `status` decides the
 * outcome family; `providerStatus` names the case within it, including the
 * held states Vision writes while LightReach has not yet answered. Once an
 * approved application has a contract, the contract decides the next step.
 * Where the kind offers Re-apply, a declined or frozen application names it,
 * or the server's reason it waits; only a kind that reports expiry reads an
 * expired one as such.
 */
export const lightReachApplicationOutcome = (
  application: LightReachApplicationView,
  contract: {
    current: LightReachContractView | null
    send: LightReachActionView
    refresh: LightReachActionView
  } | null = null,
  options: LightReachOutcomeOptions = {},
): LightReachOutcome => {
  const stipulations = application.stipulations ?? []
  const { reapply } = options
  const contractState =
    contract?.current != null
      ? lightReachContractState(contract.current, contract, {
          canSendAgain:
            options.resendAfterClose === true && contract.send.allowed,
        })
      : null
  // Declined, frozen or expired: Re-apply where the kind offers it.
  const reapplyStep = (fallback: string): string =>
    reapply == null
      ? fallback
      : reapply.allowed
        ? REAPPLY_STEP
        : reapply.reason != null
          ? reapplyBlockedStep(reapply.reason)
          : fallback
  if (
    options.reportsExpiry === true &&
    application.providerStatus === `expired`
  ) {
    return {
      badge: `Expired`,
      tone: `danger`,
      title: `The LightReach credit approval expired`,
      detail: `LightReach's credit decision for this application is no longer valid.`,
      nextStep: reapplyStep(CHOOSE_ANOTHER_PARTNER_STEP),
      supportNeeded: false,
    }
  }
  const contractStep =
    contractState != null
      ? contractState.nextStep
      : contract == null || contract.send.allowed
        ? SEND_CONTRACT_STEP
        : (contract.send.reason ?? CONTRACT_UNAVAILABLE_STEP)
  const contractSupportNeeded = contractState?.supportNeeded ?? false
  switch (application.providerStatus) {
    case `submitting`:
      return {
        badge: `Submitting`,
        tone: `muted`,
        title: `Submitting to LightReach`,
        detail: `Vision is sending the application to LightReach.`,
        nextStep: `Refresh the status in a minute. Do not apply again.`,
        supportNeeded: false,
      }
    case `submissionUnconfirmed`:
    case `submissionReported`:
      return {
        badge: `Confirming`,
        tone: `gold`,
        title: `LightReach has not confirmed the application`,
        detail: `It may still have been filed. Vision did not send it again and is confirming it with LightReach.`,
        nextStep: `Check back in a few minutes. Do not apply again.`,
        supportNeeded: false,
      }
    case `submissionNeedsReview`:
      return {
        badge: `Needs review`,
        tone: `gold`,
        title: `Powur support is confirming this application`,
        detail: `LightReach lists an application Vision cannot match to this submission.`,
        nextStep: LIGHTREACH_SUPPORT_GUIDANCE,
        supportNeeded: true,
      }
    case `approved`:
      return {
        badge: `Approved`,
        tone: `success`,
        title: `LightReach approved the application`,
        detail: `The applicant's credit is approved for the saved offer.`,
        nextStep: contractStep,
        supportNeeded: contractSupportNeeded,
      }
    case `approvedWithStipulations`: {
      const title = `LightReach approved the application with stipulations`
      // LightReach sends the contract while stipulations are open, so they
      // never hold the contract step.
      if (stipulations.length === 0) {
        // The status can land before LightReach's stipulation list does.
        return {
          badge: `Approved with stipulations`,
          tone: `gold`,
          title,
          detail: `LightReach has not listed the stipulations yet.`,
          nextStep: contractSupportNeeded
            ? contractStep
            : `${contractStep} Refresh the status in a few minutes to see what LightReach needs.`,
          supportNeeded: contractSupportNeeded,
        }
      }
      const open = stipulations.some((stipulation) => !stipulation.isSatisfied)
      return {
        badge: `Approved with stipulations`,
        tone: open ? `gold` : `success`,
        title,
        detail: open
          ? contract?.current != null && contract.current.status !== `created`
            ? `Open stipulations do not hold the contract; LightReach reviews them separately.`
            : contract == null || contract.send.allowed
              ? `Open stipulations do not hold the contract. LightReach can send it now.`
              : `Open stipulations do not hold the contract.`
          : `Every stipulation LightReach reported is satisfied.`,
        // A closed contract leaves support as the only next step.
        nextStep:
          open && !contractSupportNeeded
            ? `${contractStep} ${STIPULATION_STEP}`
            : contractStep,
        supportNeeded: contractSupportNeeded,
      }
    }
    case `declined`:
      return {
        badge: `Declined`,
        tone: `danger`,
        title: `LightReach declined the application`,
        detail: `LightReach did not approve credit for this application.`,
        nextStep: reapplyStep(CHOOSE_ANOTHER_PARTNER_STEP),
        supportNeeded: false,
      }
    case `creditFrozen`:
      return {
        badge: `Credit frozen`,
        tone: `danger`,
        title: `The applicant's credit is frozen`,
        detail: `LightReach could not pull credit while the freeze is in place.`,
        nextStep:
          reapply?.allowed === true
            ? `Ask the homeowner to lift the credit freeze, then re-apply below.`
            : reapplyStep(
                `Ask the homeowner to lift the credit freeze, then contact Powur support.`,
              ),
        supportNeeded: false,
      }
    case `applicantNotFound`:
      return {
        badge: `Applicant not found`,
        tone: `danger`,
        title: `LightReach could not find the applicant's credit file`,
        detail: `The credit bureau returned no match for the applicant's details.`,
        nextStep: `Check the homeowner's details with Powur support, or choose another Finance Partner.`,
        supportNeeded: false,
      }
    case `error`:
      return {
        badge: `Error`,
        tone: `danger`,
        title: `LightReach could not process the application`,
        detail: `LightReach reported an error for this application.`,
        nextStep: LIGHTREACH_SUPPORT_GUIDANCE,
        supportNeeded: true,
      }
    default:
      break
  }

  switch (application.status) {
    case `cancelled`:
      return {
        badge: `Closed`,
        tone: `muted`,
        title: `The application is no longer active`,
        detail: `LightReach reports it as ${application.providerStatus ?? `closed`}.`,
        nextStep: CHOOSE_ANOTHER_PARTNER_STEP,
        supportNeeded: false,
      }
    case `denied`:
      return {
        badge: `Not approved`,
        tone: `danger`,
        title: `LightReach did not approve the application`,
        detail: `LightReach reports it as ${application.providerStatus ?? `not approved`}.`,
        nextStep: CHOOSE_ANOTHER_PARTNER_STEP,
        supportNeeded: false,
      }
    default:
      return {
        badge: `Submitted`,
        tone: `muted`,
        title: `Submitted to LightReach`,
        detail: `LightReach is reviewing the application.`,
        nextStep: `Wait for LightReach's decision, then refresh the status.`,
        supportNeeded: false,
      }
  }
}

export const lightReachStipulationState = (
  stipulation: LightReachStipulationView,
): { label: string; tone: ConcertOutcomeTone } =>
  stipulation.isSatisfied
    ? { label: `Satisfied`, tone: `success` }
    : stipulation.requiresReview
      ? { label: `In review`, tone: `gold` }
      : { label: `Open`, tone: `danger` }
